/**
 * Usage Dashboard Hooks
 *
 * React Query hooks for fetching usage dashboard metrics.
 * Data is scoped by billing account with optional workspace filtering.
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query'
import type { DashboardData } from '~/components/usage-dashboard/types'
import { useBillingContext } from '~/contexts/BillingContext'
import { QueryKeys } from '~/services/queryClient'
import { getDashboardMetrics, type TimeRange } from '~/services/usageDashboardService'

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch dashboard metrics for the current billing account.
 *
 * @param timeRange - Time range filter (default: billing_cycle)
 * @param page - Leaderboard page number (default: 1)
 * @param pageSize - Leaderboard items per page (default: 10)
 * @param workspaceUuid - Optional workspace filter (undefined = all workspaces)
 * @param startDate - Optional start date for custom range (ISO string)
 * @param endDate - Optional end date for custom range (ISO string)
 * @param billingAccountIdOverride - When set (e.g. super admin), fetch metrics for this billing id instead of BillingContext
 */
export function useUsageDashboard(
  timeRange: TimeRange = 'billing_cycle',
  page: number = 1,
  pageSize: number = 10,
  workspaceUuid?: string,
  startDate?: string,
  endDate?: string,
  billingAccountIdOverride?: number | null
): UseQueryResult<DashboardData> {
  const { billingInfo } = useBillingContext()
  const billingId = billingAccountIdOverride ?? billingInfo?.id

  return useQuery({
    queryKey: [
      ...QueryKeys.UsageDashboard,
      billingId,
      billingAccountIdOverride ?? 'ctx',
      timeRange,
      page,
      pageSize,
      workspaceUuid,
      startDate,
      endDate
    ],
    queryFn: async () => {
      if (!billingId) throw new Error('No billing account selected')
      return getDashboardMetrics(billingId, {
        timeRange,
        page,
        pageSize,
        workspaceUuid,
        startDate,
        endDate
      })
    },
    enabled: !!billingId,
    // DO NOT add staleTime or caching here — dashboard data must always reflect
    // the latest billing/license state to avoid showing stale plan types.
    staleTime: 0
  })
}
